import { memo } from "react"

import { EnrollmentActionsMenu } from "@/components/layout/Common/EnrollmentActionsMenu"
import { SwimEmptyState } from "@/components/molecules/SwimEmptyState"
import type { EnrollmentResponse } from "@/types/enrollment"
import { Flex, Table } from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface EnrollmentListProps {
  enrollments: EnrollmentResponse[]
}

const EnrollmentList = memo(({ enrollments }: EnrollmentListProps) => {
  if (!enrollments?.length) {
    return <SwimEmptyState resource="enrollments" actionTo="/enrollments" />
  }

  return (
    <Table.Root size={{ base: "sm", md: "md" }}>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader w="sm">ID</Table.ColumnHeader>
          <Table.ColumnHeader>Student Id</Table.ColumnHeader>
          <Table.ColumnHeader>Class Template Id</Table.ColumnHeader>
          <Table.ColumnHeader>Session Id</Table.ColumnHeader>
          <Table.ColumnHeader>Status</Table.ColumnHeader>
          <Table.ColumnHeader>Waitlist Position</Table.ColumnHeader>
          <Table.ColumnHeader>Payment Status</Table.ColumnHeader>
          <Table.ColumnHeader>Amount Due</Table.ColumnHeader>
          <Table.ColumnHeader>Amount Paid</Table.ColumnHeader>
          <Table.ColumnHeader>Payment Method</Table.ColumnHeader>
          <Table.ColumnHeader>Payment Date</Table.ColumnHeader>
          <Table.ColumnHeader>Invoice Number</Table.ColumnHeader>
          <Table.ColumnHeader>Notes</Table.ColumnHeader>
          <Table.ColumnHeader>Created By</Table.ColumnHeader>
          <Table.ColumnHeader>Created</Table.ColumnHeader>
          <Table.ColumnHeader>Updated By</Table.ColumnHeader>
          <Table.ColumnHeader>Updated</Table.ColumnHeader>
          <Table.ColumnHeader w="xs">Actions</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {enrollments.map((enrollment) => (
          <Table.Row key={enrollment.id}>
            <Table.Cell truncate maxW="sm">
              {enrollment.id}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.student_id, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.class_template_id, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.session_id, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.status, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.waitlist_position, "int")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.invoice?.payment_status, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.invoice?.amount_due, "float")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.invoice?.amount_paid, "float")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.invoice?.payment_method, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.invoice?.payment_date, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.invoice?.invoice_number, "str")}
            </Table.Cell>
            <Table.Cell>{formatFieldValue(enrollment.notes, "str")}</Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.created_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.created_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.updated_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(enrollment.updated_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              <Flex justify="flex-end">
                <EnrollmentActionsMenu enrollment={enrollment} />
              </Flex>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
})

EnrollmentList.displayName = "EnrollmentList"

export default EnrollmentList
